import React from 'react';
import { BRAND_ICONS } from './brandIcons';
import { ANIMATED_ICONS } from './animatedIcons';
import { LUCIDE_ICONS } from './lucideIcons';
import {
  DEFAULT_ICON_SIZE,
  DEFAULT_STROKE_WIDTH,
  ICON_SIZES,
} from './taxonomy';

const REGISTRY = {
  ...LUCIDE_ICONS,
  ...BRAND_ICONS,
  ...ANIMATED_ICONS,
};

/**
 * Unified PetsFostering icon.
 *
 * @example
 *   <Icon name="paw" />
 *   <Icon name="loader-running-dog" size="2xl" title="Loading" />
 *   <Icon name="booking-confirmed" size="sm" className="text-emerald-600" />
 */
export function Icon({
  name,
  size = DEFAULT_ICON_SIZE,
  strokeWidth,
  title,
  className = '',
  decorative,
  ...rest
}) {
  const Glyph = REGISTRY[name];

  if (!Glyph) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`[Icon] Unknown icon "${name}". Check icon-metadata.json / registries.`);
    }
    return null;
  }

  const sizeToken = typeof size === 'string' ? ICON_SIZES[size] : null;
  const pixelSize = sizeToken ? sizeToken.px : typeof size === 'number' ? size : ICON_SIZES[DEFAULT_ICON_SIZE].px;
  const sizeClass = sizeToken ? sizeToken.className : '';
  const isBrandOrAnimated = Boolean(BRAND_ICONS[name] || ANIMATED_ICONS[name]);
  const isAnimated = Boolean(ANIMATED_ICONS[name]);
  const resolvedStroke = strokeWidth ?? (isAnimated ? undefined : DEFAULT_STROKE_WIDTH);
  const isDecorative = decorative ?? !title;

  const a11yProps = isDecorative
    ? { 'aria-hidden': true, focusable: 'false' }
    : isBrandOrAnimated
      ? { title }
      : { 'aria-label': title, role: 'img' };

  return (
    <Glyph
      size={pixelSize}
      strokeWidth={resolvedStroke}
      className={`${sizeClass} ${className}`.trim()}
      {...a11yProps}
      {...rest}
    />
  );
}

export function hasIcon(name) {
  return Boolean(REGISTRY[name]);
}

export function listIconNames() {
  return Object.keys(REGISTRY).sort();
}

export function getIconSource(name) {
  if (BRAND_ICONS[name]) return 'brand';
  if (ANIMATED_ICONS[name]) return 'animated';
  if (LUCIDE_ICONS[name]) return 'lucide';
  return null;
}

export default Icon;
